import {
  DesignerFeature,
  DesignerFeatures,
  DesignerParams,
  Shop,
  SprdUser,
  StoreFontFamily,
} from '@/types';
import { getDesignerType, init as initAdobe } from '@/utils/adobeLaunch';
import { assertDefined } from '@/utils/assertDefined';
import { fetchApplicationSettings } from '@/utils/http/fetchApplicationSettings';
import { getFontUrl } from '@/utils/imageUrls/getFontUrl';
import { hasUserAPI, isAdminMode, isUserAreaMode } from '@/utils/params';
import { mapDraftToRaygunDraft, setOnBeforeSend } from '@/utils/raygun';
import { getAppearance } from '@/model/draft/getAppearance';
import { getProductType } from '@/model/draft/getProductType';
import { isMdsPriceStrategy } from '@/model/pricing/isMdsPriceStrategy';
import { getPriceBreakdown } from '@/redux/common/getters/getPriceBreakdown';
import { fetchContextAction } from '@/redux/core/actions/fetchContextAction';
import { fetchCurrencyAction } from '@/redux/core/actions/fetchCurrencyAction';
import { fetchCurrencyExchangeRatesAction } from '@/redux/core/actions/fetchCurrencyExchangeRatesAction';
import { fetchFontFamiliesAction } from '@/redux/core/actions/fetchFontFamiliesAction';
import { fetchPrintTypesAction } from '@/redux/core/actions/fetchPrintTypesAction';
import { fetchSpecialFontsAction } from '@/redux/core/actions/fetchSpecialFontsAction';
import { setFeaturesAction } from '@/redux/core/actions/setFeaturesAction';
import { setUserAction } from '@/redux/core/actions/setUserAction';
import { getConfiguredFeatures } from '@/redux/core/features';
import { getCurrencyId } from '@/redux/core/getters/getCurrencyId';
import { setReduxDraftAction } from '@/redux/draft/actions/setReduxDraftAction';
import { getInitialDraft, loadLazyStore } from '@/redux/lazyStore';
import { fetchMasksAction } from '@/redux/masks/actions/fetchMasksAction';
import { fetchProductTypesRatingsAction } from '@/redux/productTypes/actions/fetchProductTypesRatingsAction';
import { StoreDispatch } from '@/redux/types';

async function getApplicationSettings(params: DesignerParams, dispatch: StoreDispatch) {
  if (params.context === 'shop' && !isAdminMode() && !isUserAreaMode()) {
    let settings: DesignerFeatures;
    try {
      settings = await fetchApplicationSettings();
    } catch (e) {
      return;
    }
    const features = getConfiguredFeatures(params.features);
    const settingsFeatures: DesignerFeatures = {};
    Object.keys(settings).forEach((key) => {
      const feature = key as DesignerFeature;
      if (features[feature] !== undefined && settings[feature] !== true) {
        settingsFeatures[feature] = settings[feature];
      }
    });
    return dispatch(setFeaturesAction(settingsFeatures));
  }
}

async function fetchExchangeRates(
  params: DesignerParams,
  context: Shop,
  dispatch: StoreDispatch
): Promise<any> {
  // international shops (all languages activated)
  if (
    params.currencyId &&
    params.currencyId !== context.currency.id &&
    isMdsPriceStrategy(context.priceStrategy)
  ) {
    return dispatch(fetchCurrencyExchangeRatesAction());
  }
  return Promise.resolve();
}

function addFontDefinitionsToHead(fontFamilies: StoreFontFamily[]) {
  const fontFaceDefinitions: string[] = [];
  fontFamilies.forEach((fontFamily) =>
    fontFamily.fonts.forEach((font) =>
      fontFaceDefinitions.push(
        `@font-face{font-family:'${font.identifier}';font-style:${font.style};font-weight:${
          font.weight
        };src:url('${getFontUrl(font.familyId, font.id)}') format('woff');}`
      )
    )
  );

  const head = assertDefined(document.querySelector('head'));
  const style = document.createElement('style');
  style.setAttribute('type', 'text/css');
  style.innerHTML = fontFaceDefinitions.join('\n');
  head.appendChild(style);
}

export async function setupStore(params: DesignerParams, name: string, activeRoute: string) {
  const [loadStore, thunkDispatch] = loadLazyStore(params);
  const initContext = thunkDispatch(fetchContextAction());
  const initSettings = getApplicationSettings(params, thunkDispatch);
  const initCurrency = initContext.then((context) => {
    return thunkDispatch(fetchCurrencyAction(params.currencyId || context.currency.id));
  });
  const initExchangeRates = initContext.then((context) =>
    fetchExchangeRates(params, context, thunkDispatch)
  );
  const initPrintTypes = thunkDispatch(fetchPrintTypesAction());
  const initFonts = thunkDispatch(fetchFontFamiliesAction()).then(addFontDefinitionsToHead);
  const initSpecialFonts = thunkDispatch(fetchSpecialFontsAction());
  const initMasks = thunkDispatch(fetchMasksAction());

  const initUser = hasUserAPI()
    ? new Promise((resolve) => {
        params.getCurrentLoginState!(null, (_, [user]: [SprdUser]) =>
          resolve(thunkDispatch(setUserAction(user)))
        );
      })
    : null;

  // not necessary to wait for response
  thunkDispatch(fetchProductTypesRatingsAction);

  const [store] = await Promise.all([
    loadStore,
    initContext,
    initSettings,
    initCurrency,
    initExchangeRates,
    initPrintTypes,
    initFonts,
    initSpecialFonts,
    initMasks,
    initUser,
  ]);

  const [initialDraft, bootstrapType] = await getInitialDraft(
    params,
    store.dispatch,
    store.getState
  );

  store.dispatch(setReduxDraftAction(initialDraft));

  const trackingStepForTemplates = params.templateId ? { stepName: 'start' } : null;
  const state = store.getState();

  initAdobe({
    applicationName: name,
    version: params.version,
    designerType: getDesignerType(),
    shopId: params.context === 'shop' ? params.contextId : undefined,
    startParameters: {
      zeroStatePanel: bootstrapType === 'empty' && !activeRoute,
      designId: params.designId,
      isReEntry: false,
      templateId: params.templateId,
    },
    products: [
      {
        productTypeId: getProductType(initialDraft).id,
        appearanceId: getAppearance(initialDraft).id,
        price: getPriceBreakdown(initialDraft, state).product,
      },
    ],
    currencyId: getCurrencyId(state),
    ...trackingStepForTemplates,
  });

  setOnBeforeSend((payload) => {
    payload.Details.UserCustomData.draft = mapDraftToRaygunDraft(store.getState().draft);
    payload.Details.UserCustomData.startParameter = params || {};
    return payload;
  });

  return [store, bootstrapType] as const;
}
